import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';
import { EventService } from 'src/app/services/events/event.service';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { Purchases, PACKAGE_TYPE, PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { SubscriptionStatus } from 'src/app/services/subscription-service/subscription.service';
import { TranslatorService } from 'src/app/services/translator-service/translator.service';
import { LoadingService } from 'src/app/services/loading-service/loading.service';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-paywall-popup',
  standalone: true,
  templateUrl: './paywall-popup.component.html',
  styleUrls: ['./paywall-popup.component.scss'],
  imports: [CommonModule, IonicModule, SharedModule],
})
export class PaywallPopupComponent implements OnInit, OnDestroy, OnChanges {
  @Input() offerings: any | null = null;
  @Input() isSubscribed = false;
  @Input() subscriptionStatus: SubscriptionStatus | null = null;

  // Local-only spinners (avoid coupling to global app loading)
  busyLoading = false;           // if you still want to reflect global loadingEvent
  busyOfferings = false;         // local: while offerings are being prepared/arriving
  busySubscribing = false;       // local: while purchase flow is in progress

  selectedPackageIdentifier: string | null = null;
  mostPopularIdentifier: string | null = null;
  privacyPolicyUrl: string = environment.privacyPolicy;
  termsOfUseURL: string = environment.termsOfUse;
  disclaimerText: string = '';

  private subs: Subscription[] = [];
  private entSub?: Subscription;
  private TAG = '[PaywallPopup]';

  private log(msg: string, extra?: any) {
    // Logging disabled - console.log removed
  }

  constructor(
    private modalCtrl: ModalController,
    private _TranslatorService: TranslatorService
  ) {
    // Optional: keep showing global loading inside modal if your app relies on it
    this.subs.push(
      EventService.loadingEvent.subscribe((val) => {
        this.busyLoading = val > 0;
      })
    );
  }

  // --- Lifecycle ---
ngOnInit(): void {
  try {
    if (!this.offerings) this.busyOfferings = true;

    // If offerings were already provided at open time, preselect now.
    if (this.offerings?.availablePackages?.length > 0) {
      this.selectDefaultFromOfferings();
    }

    this.updateDisclaimer();

    // Listen for entitlements flipping active (broadcasted by your app/service)
    // Make sure you emit EventService.revcatEntitlementsActive in your Purchases listener.
    this.entSub = EventService.revcatEntitlementsActive.subscribe(() => {
      this.isSubscribed = true;
      this.dismissWithResult(true);
    });
  } catch (e) {
    this.log('init failed', { message: (e as any)?.message });
  }
}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['offerings'] && this.offerings?.availablePackages?.length > 0) {
      this.selectDefaultFromOfferings();
      this.busyOfferings = false;
    }
  }

  ngOnDestroy(): void {
    this.entSub?.unsubscribe();
    this.subs.forEach((s) => s.unsubscribe());
  }

  // --- Helpers ---

  private selectDefaultFromOfferings(): void {
    const sorted = [...this.offerings.availablePackages].sort(
      (a: any, b: any) => (b?.product?.price ?? 0) - (a?.product?.price ?? 0)
    );
    this.selectedPackageIdentifier = sorted[0]?.identifier ?? null;
    this.mostPopularIdentifier = sorted[0]?.identifier ?? null;
    this.updateDisclaimer();
  }

  getSelectedPackage(): PurchasesPackage | undefined {
    return this.offerings?.availablePackages?.find(
      (p: PurchasesPackage) => p.identifier === this.selectedPackageIdentifier
    );
  }

  selectPackage(pkgId: string) {
    if (this.busySubscribing) return;
    this.selectedPackageIdentifier = pkgId;
    this.updateDisclaimer();
  }

  private updateDisclaimer() {
    const pkg = this.getSelectedPackage();
    let disclaimer = '';
    try {
      if (pkg?.product?.priceString) {
        let period = '';
        if (pkg.packageType == PACKAGE_TYPE.MONTHLY) {
          period = this._TranslatorService.translateText('paywall-popup.monthly');
        } else if (pkg.packageType == PACKAGE_TYPE.THREE_MONTH) {
          period = this._TranslatorService.translateText('paywall-popup.three-month');
        } else if (pkg.packageType == PACKAGE_TYPE.ANNUAL) {
          period = this._TranslatorService.translateText('paywall-popup.annual');
        }
        const pricePeriodTemplate = this._TranslatorService.translateText('paywall-popup.disclaimer-price-period');
        const pricePeriod = pricePeriodTemplate
          .replace('{price}', pkg.product.priceString)
          .replace('{period}', period);
        const bodyTemplate = this._TranslatorService.translateText(
          Capacitor.getPlatform() == 'android'
            ? 'paywall-popup.disclaimer-text-1-google'
            : 'paywall-popup.disclaimer-text-1-apple'
        );
        disclaimer = `${pricePeriod} ${bodyTemplate}`;
      }
    } catch { /* ignore */ }
    this.disclaimerText = disclaimer;
  }

  onDisclaimerLinkClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const linkType = target?.closest('[data-link]')?.getAttribute('data-link');
    if (linkType === 'terms') {
      event.preventDefault();
      void this.openUrl(this.termsOfUseURL);
    } else if (linkType === 'privacy') {
      event.preventDefault();
      void this.openUrl(this.privacyPolicyUrl);
    }
  }

  // --- Purchases flow ---
  async subscribe(pkg?: PurchasesPackage) {
    this.log('subscribe tapped', { selected: this.selectedPackageIdentifier });

    if (this.isSubscribed || this.busySubscribing) return;
    if (!pkg) pkg = this.getSelectedPackage();
    if (!pkg) { this.log('no package selected'); return; }

    // Guard for web builds where the plugin isn't available
    if (Capacitor.getPlatform() === 'web') {
      this.log('Purchases not available on web');
      return;
    }

    if (!Purchases || typeof Purchases.purchasePackage !== 'function') {
      this.log('Purchases plugin missing (import not resolved)');
      return;
    }

    this.busySubscribing = true;
    const loadingURL = 'https://paycat/subscribe';
    LoadingService.setLoading(true, loadingURL);

    const TIMEOUT_MS = 15000;
    const withTimeout = <T>(p: Promise<T>) =>
      Promise.race([
        p,
        new Promise<never>((_, rej) => setTimeout(() => rej(new Error('timeout')), TIMEOUT_MS))
      ]);

    try {
      this.log('purchasePackage call', { pkgId: pkg.identifier, productId: (pkg as any)?.product?.identifier });
      const result = await withTimeout(Purchases.purchasePackage({ aPackage: pkg }));
      // result: { customerInfo: CustomerInfo }

      // (1) Check entitlements from purchase result
      const active1 = result?.customerInfo?.entitlements?.active ?? {};
      if (Object.keys(active1).length > 0) {
        this.isSubscribed = true;
        await this.dismissWithResult(true);
        return;
      }

      // (2) Re-fetch to beat propagation delays
      const refreshed = await withTimeout(Purchases.getCustomerInfo());
      // refreshed: { customerInfo: CustomerInfo }
      const active2 = refreshed?.customerInfo?.entitlements?.active ?? {};

      if (Object.keys(active2).length > 0) {
        this.isSubscribed = true;
        await this.dismissWithResult(true);
        return;
      }

      this.log('No active entitlements after purchase');
    } catch (err: any) {
      const code = err?.code ?? err?.message;
      if (code === 'PURCHASE_CANCELLED_ERROR' || code === '1' /* iOS cancel */) {
        this.log('Purchase cancelled by user');
      } else if (err?.message === 'timeout') {
        this.log('Purchase flow timeout');
        // Optional: show a toast "Still finalizing your purchase…"
      } else {
        this.log('purchase error', { code: err?.code, message: err?.message });
      }
    } finally {
      LoadingService.setLoading(false, loadingURL);
      this.busySubscribing = false;
    }
  }


  // --- Modal controls ---

  async dismissWithResult(result: boolean) {
    // Using controller form with id (3-arg) is correct here
    await this.modalCtrl.dismiss(
      { success: result },
      result ? 'success' : 'cancelled',
      'paywall-modal'
    );
    this.log('modal dismissed', { role: result ? 'success' : 'cancelled' });
  }

  async openUrl(theUrl: string) {
    if (!theUrl?.trim()) return;
    try { await Browser.open({ url: theUrl }); } catch { /* ignore */ }
  }
}